'use client';

import styled from '@emotion/styled';
import React, { useCallback, useMemo } from 'react';
import { ContentFilterType, ContentOrderType, RelayType } from '@diritto/common/content/constants';
import { CategoryNavigationMenu } from '@diritto/common/navigation/constant';
import { ExploreNavigationMenu, ExploreSubMenu } from '@diritto/common/navigation/explore';
import { ContentPublicRelayDto, ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodeRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { PersonalContentRelayDto } from '@diritto/common/relay/dtos/personal-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { ContentListType, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { accurateItems, flatItems } from '@diritto/common/utils/array';
import { ExploreContentItem } from 'app-components/explore/ExploreContentItem';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useGetRelayContentsInfiniteQuery } from 'queries/contents/useGetRelayContentsInfiniteQuery';
import { useBlockProfiles } from 'state-hooks/accounts/useBlockProfiles';
import { EmptyList } from 'ui-components/common/EmptyList';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { Loading } from 'ui-components/common/Loading';

interface LatestContentListViewProps {
  categoryId?: number;
  categoryIds?: number[];
  filterType: ContentFilterType;
  orderType: ContentOrderType;
  categoryNavMenu: CategoryNavigationMenu;
  exploreMenu: ExploreNavigationMenu;
  exploreSubMenu?: ExploreSubMenu;
}

const trackerExtraInfo = {
  trackerPosition: TrackerPosition.Explorer,
};

export const ExploreContentList = ({
  categoryId,
  categoryIds,
  filterType,
  orderType,
  categoryNavMenu,
  exploreMenu,
  exploreSubMenu,
}: LatestContentListViewProps) => {
  const { tracker } = useTrackingState();
  const { includeAdult } = useAuthState();
  const blockedProfileIds = useBlockProfiles();

  const { data, hasNextPage, isLoading, fetchNextPage } = useGetRelayContentsInfiniteQuery({
    categoryId,
    categoryIds: categoryIds?.join(','),
    orderType,
    filterType,
    includeAdult,
  });

  const handelClick = useCallback(
    (index: number, content: ContentPublicRelayDto) => {
      tracker.track(TrackingEventType.SelectContentListItem, {
        listType: ContentListType.Explore,
        listItemIndex: index as number,
        contentId: content.id,
        params: {
          categoryMenu: categoryNavMenu,
          exploreMenu,
          exploreSubMenu,
        },
        trackerExtraInfo,
      });
    },
    [categoryNavMenu, exploreMenu, exploreSubMenu, tracker],
  );

  const { contentIds, contents, profiles } = useMemo(() => {
    const ids = flatItems<number>('contentIds', data?.pages) ?? [];
    return {
      contentIds: [...new Set(ids)],
      contents: accurateItems<ContentRelayDto>('contents', data?.pages),
      episodes: accurateItems<EpisodeRelayDto>('episodes', data?.pages),
      profiles: accurateItems<ProfileLiteRelayDto>('profileLites', data?.pages),
      personalContents: accurateItems<PersonalContentRelayDto>('personalContents', data?.pages),
    };
  }, [data]);

  if (isLoading) {
    return <StyledLoading />;
  }

  return (
    <StyledContentList>
      {contentIds && (
        <InfiniteScrollWrapper hasNext={hasNextPage} loadMore={fetchNextPage}>
          {contentIds.length === 0 && <EmptyList description="아직 작품이 없어요." />}
          {contentIds.length > 0 && (
            <StyledList>
              {contentIds.map((id, idx) => {
                const content: ContentRelayDto = contents[id];
                const profile =
                  content?.relayType === RelayType.Public && content.profileId
                    ? profiles[content.profileId]
                    : undefined;

                if ((profile && profile.isDeleted) || !content || content.relayType !== RelayType.Public) {
                  return null;
                }

                if (profile && blockedProfileIds.has(profile.id)) {
                  return null;
                }

                return (
                  <ExploreContentItem
                    key={id}
                    listItemIndex={idx}
                    content={content}
                    profile={profile}
                    onClick={handelClick}
                  />
                );
              })}
            </StyledList>
          )}
        </InfiniteScrollWrapper>
      )}
    </StyledContentList>
  );
};

const StyledContentList = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const StyledLoading = styled(Loading)`
  display: flex;
  height: 50vh;
`;

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
